import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import CatalogProductDetail from "@/components/CatalogProductDetail";

/**
 * CatalogPicker · full-dynamic catalog browser (Feb 2026).
 *
 * The entire catalog is mirrored to MongoDB by
 * `catalog_engine/sanmar_sync.py`. This component reads two endpoints:
 *
 *   GET /api/catalog/browse/facets           → categories + brands tree
 *   GET /api/catalog/browse/search           → paginated product list
 *
 * There is NO curated list, NO "popular" chips, NO supplier branding,
 * NO external vendor link. Colors are rendered from the ACTUAL swatch
 * image URL (`swatch_url`) returned by the sync — never a gray fallback.
 *
 * Admin users see a small sync panel in the header that polls
 * `/api/catalog/admin/sync-status` and can force a fresh pull.
 */

const PAGE_SIZE = 48;

// Defensive HTML-entity decoder — SanMar sometimes returns strings like
// "Infant &amp; Toddler". The backend normalizes on sync, but this is our
// last line of defense so a raw entity NEVER reaches the user.
const _decoderEl = typeof document !== "undefined" ? document.createElement("textarea") : null;
function decodeHtml(str) {
  if (typeof str !== "string" || !str) return str;
  if (!_decoderEl) return str;
  _decoderEl.innerHTML = str;
  return _decoderEl.value;
}

function useDebounce(value, delay = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return v;
}

function ProductCard({ item, onOpen, t }) {
  const primaryImage =
    item.default_image ||
    item.colors?.[0]?.image ||
    item.colors?.[0]?.swatch ||
    "";
  const previewColors = (item.colors || []).slice(0, 6);
  return (
    <button
      type="button"
      onClick={() => onOpen(item)}
      data-testid={`catalog-card-${item.style}`}
      className="group text-left border border-[var(--border)] bg-white hover:border-black transition-colors flex flex-col overflow-hidden"
    >
      <div className="aspect-[4/3] bg-[var(--surface-alt)] overflow-hidden">
        {primaryImage ? (
          <img
            src={primaryImage}
            alt={item.title}
            loading="lazy"
            onError={(e) => {
              e.currentTarget.style.opacity = "0.35";
            }}
            className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-xs text-[var(--text-secondary)]">
            —
          </div>
        )}
      </div>
      <div className="p-3 flex flex-col gap-1.5 flex-1">
        {item.brand && (
          <div className="font-mono-tech text-[0.6rem] font-bold tracking-[0.22em] text-[var(--text-secondary)] uppercase truncate">
            {item.brand}
          </div>
        )}
        <div className="font-display text-sm font-bold leading-snug line-clamp-2 min-h-[2.5rem]">
          {item.title || item.style}
        </div>
        <div className="font-mono-tech text-[0.65rem] font-bold tracking-[0.15em] text-[var(--text-secondary)]">
          {item.style}
        </div>
        {previewColors.length > 0 && (
          <div className="flex items-center gap-1.5 mt-1">
            {previewColors.map((c, i) => (
              <span
                key={`${c.name}-${i}`}
                title={c.name}
                className="inline-block w-4 h-4 rounded-full border border-[var(--border)] bg-[var(--surface-alt)] bg-center bg-cover"
                style={c.swatch ? { backgroundImage: `url(${c.swatch})` } : {}}
              />
            ))}
            {item.color_count > previewColors.length && (
              <span className="font-mono-tech text-[10px] font-bold text-[var(--text-secondary)]">
                +{item.color_count - previewColors.length}
              </span>
            )}
          </div>
        )}
        <div className="flex items-center justify-between mt-1.5">
          <span className="font-mono-tech text-[0.65rem] text-[var(--text-secondary)]">
            {item.color_count || 0} colores · {item.size_count || 0} tallas
          </span>
          {item.piece_price != null ? (
            <span className="font-display text-base font-black leading-none">
              ${Number(item.piece_price).toFixed(2)}
            </span>
          ) : (
            <span
              data-testid="catalog-card-consult-price"
              className="font-mono-tech text-[0.6rem] font-bold uppercase text-[var(--text-secondary)]"
            >
              Consultar precio
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

function AdminSyncPanel({ t }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/catalog/admin/sync-status`, {
        withCredentials: true,
      });
      setStatus(data);
      return data;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    // Poll every 3s while running
    pollRef.current = setInterval(async () => {
      const s = await fetchStatus();
      if (s && s.state !== "running") {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    }, 3000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [fetchStatus]);

  const runSync = async (force = false) => {
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/catalog/admin/sync-sanmar`,
        null,
        { withCredentials: true, params: { force } }
      );
      setStatus(data.status);
      if (data.started) {
        toast.success(t("catalog_sync_running"));
      }
      if (!pollRef.current) {
        pollRef.current = setInterval(async () => {
          const s = await fetchStatus();
          if (s && s.state !== "running") {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
        }, 3000);
      }
    } catch {
      toast.error(t("catalog_sync_error"));
    } finally {
      setBusy(false);
    }
  };

  if (!status) return null;
  const running = status.state === "running";
  const pct =
    status.total > 0
      ? Math.min(100, Math.round((status.processed / status.total) * 100))
      : 0;

  return (
    <div
      data-testid="catalog-admin-sync-panel"
      className="border border-[var(--border)] bg-[var(--surface-alt)] px-3 py-2 flex flex-col gap-1.5 min-w-[240px]"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="font-mono-tech text-[0.6rem] font-bold tracking-[0.22em] text-[var(--text-secondary)]">
          {t("catalog_admin_panel_title")}
        </div>
        <span
          className={`font-mono-tech text-[0.6rem] font-bold tracking-[0.18em] px-1.5 py-0.5 ${
            running
              ? "bg-black text-white animate-pulse"
              : status.state === "error"
              ? "bg-red-600 text-white"
              : "bg-emerald-600 text-white"
          }`}
        >
          {running
            ? t("catalog_sync_running")
            : status.state === "error"
            ? t("catalog_sync_error")
            : t("catalog_sync_ready")}
        </span>
      </div>
      {running && (
        <>
          <div className="h-1.5 bg-white border border-[var(--border)] overflow-hidden">
            <div
              className="h-full bg-black transition-all duration-500"
              style={{ width: `${pct}%` }}
              data-testid="catalog-admin-sync-progress"
            />
          </div>
          <div className="font-mono-tech text-[0.65rem] text-[var(--text-secondary)]">
            {status.processed} / {status.total} ({pct}%)
          </div>
        </>
      )}
      {!running && (
        <div className="flex items-center justify-between font-mono-tech text-[0.65rem]">
          <span className="text-[var(--text-secondary)]">
            {status.catalog_count} {t("catalog_admin_products")}
          </span>
          <button
            type="button"
            onClick={() => runSync(false)}
            disabled={busy}
            data-testid="catalog-admin-run-sync"
            className="font-display text-[0.65rem] font-bold px-2 py-1 border border-black hover:bg-black hover:text-white transition-colors"
          >
            {t("catalog_admin_run_sync")}
          </button>
        </div>
      )}
      <div className="font-mono-tech text-[0.55rem] text-[var(--text-secondary)] tracking-[0.15em]">
        {t("catalog_admin_scheduled")}
      </div>
    </div>
  );
}

export default function CatalogPicker({ open, onClose, onAdd, readOnly = false }) {
  const { t } = useI18n();
  const { isAdmin } = useAuth();

  // Facets
  const [facets, setFacets] = useState({ categories: [], brands: [], total_styles: 0 });
  const [facetsLoading, setFacetsLoading] = useState(false);

  // Filters
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query, 350);
  const [category, setCategory] = useState("");
  const [subCategory, setSubCategory] = useState("");
  const [brand, setBrand] = useState("");
  const [page, setPage] = useState(1);

  // Results
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [loading, setLoading] = useState(false);

  // Product detail overlay
  const [selected, setSelected] = useState(null);

  // Reset when reopened
  useEffect(() => {
    if (!open) return;
    setQuery("");
    setCategory("");
    setSubCategory("");
    setBrand("");
    setPage(1);
    setSelected(null);
  }, [open]);

  // Load facets once
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setFacetsLoading(true);
    axios
      .get(`${API}/catalog/browse/facets`, { withCredentials: true })
      .then(({ data }) => {
        if (cancelled) return;
        // Defensively decode any HTML entities that snuck through
        const cats = (data.categories || []).map((c) => ({
          ...c,
          name: decodeHtml(c.name),
          subcategories: (c.subcategories || []).map((s) => ({
            ...s,
            name: decodeHtml(s.name),
          })),
        }));
        const brands = (data.brands || []).map((b) => ({
          ...b,
          name: decodeHtml(b.name),
        }));
        setFacets({ ...data, categories: cats, brands });
      })
      .catch(() => {
        if (!cancelled) setFacets({ categories: [], brands: [], total_styles: 0 });
      })
      .finally(() => {
        if (!cancelled) setFacetsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Load results whenever filters change
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    const params = { page, limit: PAGE_SIZE };
    if (debouncedQuery.trim()) params.q = debouncedQuery.trim();
    if (category) params.category = category;
    if (subCategory) params.sub_category = subCategory;
    if (brand) params.brand = brand;
    axios
      .get(`${API}/catalog/browse/search`, {
        params,
        withCredentials: true,
      })
      .then(({ data }) => {
        if (cancelled) return;
        const decoded = (data.results || []).map((r) => ({
          ...r,
          title: decodeHtml(r.title),
          brand: decodeHtml(r.brand),
          category: decodeHtml(r.category),
          sub_category: decodeHtml(r.sub_category),
        }));
        setResults(decoded);
        setTotal(data.total || 0);
        setPages(data.pages || 0);
      })
      .catch(() => {
        if (!cancelled) {
          setResults([]);
          setTotal(0);
          setPages(0);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, debouncedQuery, category, subCategory, brand, page]);

  // When user changes category/brand/query → reset to page 1
  useEffect(() => {
    setPage(1);
  }, [debouncedQuery, category, subCategory, brand]);

  const activeCategory = useMemo(
    () => facets.categories.find((c) => c.name === category),
    [facets, category]
  );

  const handleAddFromDetail = (items) => {
    onAdd(items);
    setSelected(null);
    onClose?.();
  };

  const clearFilters = () => {
    setQuery("");
    setCategory("");
    setSubCategory("");
    setBrand("");
    setPage(1);
  };

  if (!open) return null;

  return (
    <>
      <div
        data-testid="catalog-picker-overlay"
        className="fixed inset-0 z-50 bg-white flex flex-col"
      >
        {/* HEADER */}
        <header className="px-4 sm:px-6 py-3 border-b border-black flex items-center justify-between bg-white flex-shrink-0 gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={onClose}
              data-testid="catalog-picker-close"
              className="font-display text-xs font-bold border border-black px-3 py-1.5 hover:bg-black hover:text-white transition-colors"
            >
              ← {t("catalog_back_to_order")}
            </button>
            <div className="min-w-0">
              <h1
                className="font-display text-lg sm:text-xl font-black tracking-tight leading-none truncate"
                data-testid="catalog-picker-title"
              >
                {t("catalog_full_title")}
              </h1>
              <div className="font-mono-tech text-[0.6rem] font-bold tracking-[0.2em] text-[var(--text-secondary)] mt-0.5">
                {facets.total_styles.toLocaleString()} {t("catalog_admin_products")}
              </div>
            </div>
          </div>
          {isAdmin && <AdminSyncPanel t={t} />}
        </header>

        {/* SEARCH BAR */}
        <div className="px-4 sm:px-6 py-3 border-b border-[var(--border)] bg-white flex-shrink-0">
          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("catalog_search_placeholder")}
              data-testid="catalog-search-input"
              className="flex-1 min-w-[240px] px-3 py-2 border border-black bg-white focus:outline-none font-display text-sm"
            />
            {(query || category || subCategory || brand) && (
              <button
                type="button"
                onClick={clearFilters}
                data-testid="catalog-clear-filters"
                className="font-display text-xs font-bold border border-black px-3 py-2 hover:bg-black hover:text-white transition-colors"
              >
                {t("catalog_filter_clear")}
              </button>
            )}
          </div>
          {/* Active-filter chips */}
          {(category || subCategory || brand) && (
            <div className="flex flex-wrap gap-2 mt-2">
              {category && (
                <button
                  type="button"
                  onClick={() => {
                    setCategory("");
                    setSubCategory("");
                  }}
                  data-testid="catalog-active-filter-category"
                  className="font-mono-tech text-[0.65rem] font-bold tracking-[0.15em] uppercase px-2 py-1 bg-black text-white hover:bg-[var(--text-secondary)]"
                >
                  {t("catalog_filter_category")}: {category} ✕
                </button>
              )}
              {subCategory && (
                <button
                  type="button"
                  onClick={() => setSubCategory("")}
                  data-testid="catalog-active-filter-subcategory"
                  className="font-mono-tech text-[0.65rem] font-bold tracking-[0.15em] uppercase px-2 py-1 bg-black text-white hover:bg-[var(--text-secondary)]"
                >
                  {t("catalog_filter_subcategory")}: {subCategory} ✕
                </button>
              )}
              {brand && (
                <button
                  type="button"
                  onClick={() => setBrand("")}
                  data-testid="catalog-active-filter-brand"
                  className="font-mono-tech text-[0.65rem] font-bold tracking-[0.15em] uppercase px-2 py-1 bg-black text-white hover:bg-[var(--text-secondary)]"
                >
                  {t("catalog_filter_brand")}: {brand} ✕
                </button>
              )}
            </div>
          )}
        </div>

        {/* BODY: left rail + results */}
        <div className="flex-1 min-h-0 flex overflow-hidden">
          {/* LEFT RAIL */}
          <aside
            data-testid="catalog-left-rail"
            className="hidden md:flex flex-col w-72 border-r border-[var(--border)] bg-[var(--surface-alt)] overflow-y-auto"
          >
            {/* Categories */}
            <section className="p-4 border-b border-[var(--border)]">
              <div className="label-overline mb-2">
                {t("catalog_pane_categories")}
              </div>
              {facetsLoading && facets.categories.length === 0 ? (
                <div className="font-mono-tech text-[0.7rem] text-[var(--text-secondary)]">
                  {t("catalog_searching")}
                </div>
              ) : (
                <div className="flex flex-col gap-0.5">
                  <button
                    type="button"
                    onClick={() => {
                      setCategory("");
                      setSubCategory("");
                    }}
                    data-testid="catalog-cat-all"
                    className={`text-left font-display text-xs font-bold px-2 py-1.5 transition-colors ${
                      !category
                        ? "bg-black text-white"
                        : "hover:bg-white text-[var(--text-primary)]"
                    }`}
                  >
                    {t("catalog_pane_all")}
                  </button>
                  {facets.categories.map((c) => (
                    <div key={c.name}>
                      <button
                        type="button"
                        onClick={() => {
                          setCategory(c.name);
                          setSubCategory("");
                        }}
                        data-testid={`catalog-cat-${c.name.replace(/[^A-Za-z0-9]/g, "_")}`}
                        className={`w-full flex items-center justify-between text-left font-display text-xs font-bold px-2 py-1.5 transition-colors ${
                          category === c.name
                            ? "bg-black text-white"
                            : "hover:bg-white text-[var(--text-primary)]"
                        }`}
                      >
                        <span className="truncate">{c.name}</span>
                        <span className="font-mono-tech text-[0.6rem] opacity-70 ml-2">
                          {c.total}
                        </span>
                      </button>
                      {/* Subcategories under active category */}
                      {category === c.name &&
                        activeCategory?.subcategories?.length > 0 && (
                          <div className="pl-3 border-l border-[var(--border)] ml-2 my-1">
                            {activeCategory.subcategories.map((s) => (
                              <button
                                key={s.name || "__none__"}
                                type="button"
                                onClick={() => setSubCategory(s.name || "")}
                                data-testid={`catalog-sub-${(s.name || "none").replace(/[^A-Za-z0-9]/g, "_")}`}
                                className={`w-full flex items-center justify-between text-left font-mono-tech text-[0.7rem] font-bold px-2 py-1 transition-colors ${
                                  subCategory === s.name
                                    ? "bg-black text-white"
                                    : "hover:bg-white text-[var(--text-secondary)]"
                                }`}
                              >
                                <span className="truncate">
                                  {s.name || "—"}
                                </span>
                                <span className="opacity-70 ml-2">{s.count}</span>
                              </button>
                            ))}
                          </div>
                        )}
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Brands */}
            <section className="p-4">
              <div className="label-overline mb-2">
                {t("catalog_pane_brands")}
              </div>
              {facets.brands.length === 0 ? (
                <div className="font-mono-tech text-[0.7rem] text-[var(--text-secondary)]">
                  {t("catalog_searching")}
                </div>
              ) : (
                <div className="flex flex-col gap-0.5">
                  <button
                    type="button"
                    onClick={() => setBrand("")}
                    data-testid="catalog-brand-all"
                    className={`text-left font-display text-xs font-bold px-2 py-1.5 transition-colors ${
                      !brand
                        ? "bg-black text-white"
                        : "hover:bg-white text-[var(--text-primary)]"
                    }`}
                  >
                    {t("catalog_pane_all")}
                  </button>
                  {facets.brands.map((b) => (
                    <button
                      key={b.name}
                      type="button"
                      onClick={() => setBrand(b.name)}
                      data-testid={`catalog-brand-${b.name.replace(/[^A-Za-z0-9]/g, "_")}`}
                      className={`flex items-center justify-between text-left font-display text-xs font-bold px-2 py-1.5 transition-colors ${
                        brand === b.name
                          ? "bg-black text-white"
                          : "hover:bg-white text-[var(--text-primary)]"
                      }`}
                    >
                      <span className="truncate">{b.name}</span>
                      <span className="font-mono-tech text-[0.6rem] opacity-70 ml-2">
                        {b.count}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </section>
          </aside>

          {/* MAIN: results */}
          <main className="flex-1 min-w-0 overflow-y-auto">
            {/* Mobile filter chips row */}
            <div className="md:hidden px-4 py-3 border-b border-[var(--border)] flex gap-2 overflow-x-auto">
              <select
                value={category}
                onChange={(e) => {
                  setCategory(e.target.value);
                  setSubCategory("");
                }}
                data-testid="catalog-mobile-category-select"
                className="border border-black text-xs font-display font-bold px-2 py-1.5 bg-white"
              >
                <option value="">{t("catalog_filter_category")}</option>
                {facets.categories.map((c) => (
                  <option key={c.name} value={c.name}>
                    {c.name} ({c.total})
                  </option>
                ))}
              </select>
              <select
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
                data-testid="catalog-mobile-brand-select"
                className="border border-black text-xs font-display font-bold px-2 py-1.5 bg-white"
              >
                <option value="">{t("catalog_filter_brand")}</option>
                {facets.brands.map((b) => (
                  <option key={b.name} value={b.name}>
                    {b.name} ({b.count})
                  </option>
                ))}
              </select>
            </div>

            {/* Result meta */}
            <div className="px-4 sm:px-6 py-3 flex items-center justify-between border-b border-[var(--border)]">
              <div
                className="font-mono-tech text-[0.7rem] font-bold tracking-[0.18em] text-[var(--text-secondary)]"
                data-testid="catalog-result-count"
              >
                {loading
                  ? t("catalog_searching")
                  : `${total.toLocaleString()} ${t("catalog_results")}`}
              </div>
            </div>

            {/* Grid */}
            {loading && results.length === 0 ? (
              <div
                data-testid="catalog-loading"
                className="p-10 text-center text-[var(--text-secondary)]"
              >
                <div className="text-3xl mb-2 animate-pulse">⏳</div>
                <div className="font-display text-sm font-bold">
                  {t("catalog_searching")}
                </div>
              </div>
            ) : results.length === 0 ? (
              <div
                data-testid="catalog-empty-state"
                className="p-10 text-center"
              >
                <div className="font-display text-lg font-bold mb-1">
                  {facets.total_styles === 0
                    ? t("catalog_empty_state_title")
                    : t("catalog_no_results")}
                </div>
                <div className="font-mono-tech text-xs text-[var(--text-secondary)]">
                  {facets.total_styles === 0
                    ? t("catalog_empty_state_hint")
                    : ""}
                </div>
              </div>
            ) : (
              <div
                data-testid="catalog-results-grid"
                className="p-4 sm:p-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4"
              >
                {results.map((r) => (
                  <ProductCard
                    key={r.style}
                    item={r}
                    onOpen={setSelected}
                    t={t}
                  />
                ))}
              </div>
            )}

            {/* Pagination */}
            {pages > 1 && (
              <div
                data-testid="catalog-pagination"
                className="p-4 flex items-center justify-center gap-2 border-t border-[var(--border)]"
              >
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  data-testid="catalog-pagination-prev"
                  className="font-display text-xs font-bold border border-black px-3 py-1.5 hover:bg-black hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  ← {t("catalog_pagination_prev")}
                </button>
                <span
                  className="font-mono-tech text-xs font-bold tracking-[0.15em]"
                  data-testid="catalog-pagination-info"
                >
                  {t("catalog_pagination_page")} {page} / {pages}
                </span>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(pages, p + 1))}
                  disabled={page >= pages}
                  data-testid="catalog-pagination-next"
                  className="font-display text-xs font-bold border border-black px-3 py-1.5 hover:bg-black hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  {t("catalog_pagination_next")} →
                </button>
              </div>
            )}
          </main>
        </div>
      </div>

      {selected && (
        <CatalogProductDetail
          summary={{
            style: selected.style,
            name: selected.title,
            brand: selected.brand,
            category: selected.category,
            default_image_url: selected.default_image,
          }}
          onClose={() => setSelected(null)}
          onAdd={handleAddFromDetail}
          readOnly={readOnly}
        />
      )}
    </>
  );
}
